/**
 * 商談詳細「顧客」タブ。
 *  - mail_contact のサマリ表示
 *  - 「顧客詳細を開く」で /sales/contacts/:contactId に遷移 (P-C 連携)
 *  - 譲渡履歴 / 重複候補は将来の実装余地
 */
import type { MailContact } from '@goenhub/shared';
import { getDefaultBillingAddress } from '@goenhub/shared';
import {
  ArrowRight,
  Building2,
  ExternalLink,
  Mail,
  MapPin,
  Phone,
  Tag,
  User,
  UserCircle2,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { EmptyState } from '@/components/common/EmptyState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { paths } from '@/routes/paths';

interface Props {
  contact: (MailContact & { id: string }) | null | undefined;
}

export function DealContactTab({ contact }: Props) {
  if (!contact) {
    return (
      <EmptyState
        icon={User}
        title="顧客情報なし"
        description="商談に紐付く mail_contact が見つかりません。"
      />
    );
  }

  const isBtoB = contact.kind === 'btob';
  const promotion = contact.promotionLevel ?? 'lead';
  const billing = getDefaultBillingAddress(contact);
  const fullAddress = [
    billing?.postalCode ? `〒${billing.postalCode}` : '',
    billing?.prefecture,
    billing?.address,
    billing?.buildingName,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="space-y-4">
      {/* ヘッダ + 詳細リンク */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isBtoB ? (
            <Building2 className="size-4 text-[var(--accent)]" />
          ) : (
            <UserCircle2 className="size-4 text-[var(--accent-purple)]" />
          )}
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">
            {contact.name || '(無名)'}
          </h3>
          <Badge variant="default">
            {contact.kind === 'btob'
              ? 'BtoB'
              : contact.kind === 'btoc'
                ? 'BtoC'
                : 'Unknown'}
          </Badge>
          {promotion === 'inactive' ? (
            <Badge variant="default">休眠</Badge>
          ) : promotion === 'customer' ? (
            <Badge variant="approved">顧客</Badge>
          ) : (
            <Badge variant="draft">リード</Badge>
          )}
        </div>
        <Button variant="primary" size="sm" asChild>
          <Link
            to={paths.salesContactDetail.replace(':contactId', contact.id)}
          >
            <ExternalLink className="size-3.5" />
            顧客詳細を開く
            <ArrowRight className="size-3" />
          </Link>
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* 連絡先 */}
        <Section title="連絡先">
          <KV
            icon={Mail}
            label="メール"
            value={contact.defaultEmail || '—'}
          />
          {contact.emails.length > 1 && (
            <KV
              icon={Mail}
              label="その他のメール"
              value={`${contact.emails.length - 1} 件`}
            />
          )}
          <KV
            icon={Phone}
            label="電話"
            value={contact.customFields?.phone ?? '—'}
          />
        </Section>

        {/* 法人情報 */}
        <Section title="法人情報">
          <KV
            icon={Building2}
            label="会社名"
            value={contact.companyName ?? '—'}
          />
          <KV
            icon={User}
            label="部署"
            value={contact.department ?? '—'}
          />
          <KV
            icon={User}
            label="役職"
            value={contact.jobTitle ?? '—'}
          />
        </Section>

        {/* 住所 */}
        <Section title="住所">
          {fullAddress ? (
            <div className="flex items-start gap-2 text-sm text-[var(--text-secondary)]">
              <MapPin className="size-3.5 text-[var(--text-muted)] mt-0.5 flex-shrink-0" />
              <span>{fullAddress}</span>
            </div>
          ) : (
            <span className="text-sm text-[var(--text-muted)]">未登録</span>
          )}
        </Section>

        {/* タグ */}
        <Section title="タグ">
          {contact.tags && contact.tags.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {contact.tags.map((t) => (
                <Badge key={t} variant="default">
                  <Tag className="size-3 mr-0.5" />
                  {t}
                </Badge>
              ))}
            </div>
          ) : (
            <span className="text-sm text-[var(--text-muted)]">なし</span>
          )}
        </Section>

        {/* 営業背景記録 */}
        <Section title="営業背景記録 (salesMemo)" full>
          {contact.salesMemo ? (
            <p className="text-sm text-[var(--text-primary)] whitespace-pre-wrap">
              {contact.salesMemo}
            </p>
          ) : (
            <span className="text-sm text-[var(--text-muted)]">
              「顧客詳細」の基本情報タブから記入できます。
            </span>
          )}
        </Section>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// 共通
// ─────────────────────────────────────────────

function Section({
  title,
  full,
  children,
}: {
  title: string;
  full?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-4 space-y-2 ${
        full ? 'lg:col-span-2' : ''
      }`}
    >
      <h4 className="text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider">
        {title}
      </h4>
      {children}
    </div>
  );
}

function KV({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Mail;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <Icon className="size-3.5 text-[var(--text-muted)] flex-shrink-0" />
      <span className="text-[var(--text-muted)] text-xs w-20 flex-shrink-0">
        {label}
      </span>
      <span className="text-[var(--text-secondary)] truncate">{value}</span>
    </div>
  );
}
